import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Droplets, Zap, Activity, Leaf, Plane } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal, Kicker, SectionTitle } from "./Reveal";

const DropCounter = () => {
  const { t } = useLang();
  const [n, setN] = useState(0);
  const add = () => setN((v) => (v >= 20 ? 0 : v + 1));
  return (
    <button onClick={add} data-testid="bento-drop-counter" className="group relative flex w-full items-end justify-between text-left">
      <div>
        <p className="font-display text-7xl leading-none text-[#7ED9D2] md:text-8xl">
          {n}<span className="text-3xl text-[#FBF7EF]/60">/20</span>
        </p>
        <p className="mt-2 font-mono-x text-xs uppercase tracking-[0.2em] text-[#FBF7EF]/70">{t.bento.dropLabel} · {t.bento.dropHint}</p>
      </div>
      <div className="relative h-28 w-16 overflow-hidden rounded-b-2xl rounded-t-md border-2 border-[#FBF7EF]/40">
        <motion.div className="absolute inset-x-0 bottom-0 bg-[#0A7189]" animate={{ height: `${(n / 20) * 100}%` }} transition={{ type: "spring", stiffness: 90, damping: 14 }} />
        <AnimatePresence>
          <motion.span key={n} className="absolute left-1/2 top-0 h-3 w-2 -translate-x-1/2 rounded-full bg-[#7ED9D2]"
            initial={{ y: -12, opacity: 1 }} animate={{ y: 100, opacity: 0 }} transition={{ duration: 0.6 }} />
        </AnimatePresence>
      </div>
    </button>
  );
};

const Card = ({ className, children, testid }) => (
  <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.3 }} data-testid={testid}
    className={`relative overflow-hidden rounded-3xl p-7 md:p-8 ${className}`}>
    {children}
  </motion.div>
);

export const Bento = () => {
  const { t } = useLang();
  const it = t.bento.items;
  return (
    <section id="benefits" className="bg-[#FBF7EF] py-24 md:py-32" data-testid="benefits-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="mb-14 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3"><Kicker>{t.bento.kicker}</Kicker></div>
          <SectionTitle className="lg:col-span-9">{t.bento.title}</SectionTitle>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:auto-rows-[minmax(240px,auto)]">
          <Reveal className="md:col-span-4 md:row-span-2">
            <Card testid="bento-hydration" className="grain flex h-full min-h-[460px] flex-col justify-between bg-[#073B4C] text-[#FBF7EF]">
              <img src="/img/drink.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#073B4C] via-[#073B4C]/60 to-transparent" />
              <div className="relative">
                <Droplets className="h-7 w-7 text-[#7ED9D2]" />
                <h3 className="mt-5 max-w-md font-display text-4xl leading-tight md:text-5xl">{it[0].t}</h3>
                <p className="mt-3 max-w-md text-[#FBF7EF]/80">{it[0].d}</p>
              </div>
              <div className="relative mt-10"><DropCounter /></div>
            </Card>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-2">
            <Card testid="bento-magnesium" className="h-full bg-[#7ED9D2]">
              <Zap className="h-6 w-6 text-[#073B4C]" />
              <p className="mt-4 font-display text-6xl leading-none">102<span className="text-2xl">mg</span></p>
              <h3 className="mt-3 text-xl font-semibold">{it[1].t}</h3>
              <p className="mt-2 text-sm text-[#073B4C]/80">{it[1].d}</p>
            </Card>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-2">
            <Card testid="bento-recovery" className="h-full bg-[#0A7189] text-[#FBF7EF]">
              <Activity className="h-6 w-6 text-[#7ED9D2]" />
              <h3 className="mt-4 text-xl font-semibold">{it[2].t}</h3>
              <p className="mt-2 text-sm text-[#FBF7EF]/85">{it[2].d}</p>
            </Card>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-3">
            <Card testid="bento-pure" className="flex h-full items-center gap-6 border border-[#073B4C]/12 bg-white/60">
              <div className="min-w-0 flex-1">
                <Leaf className="h-6 w-6 text-[#0A7189]" />
                <h3 className="mt-4 text-xl font-semibold">{it[3].t}</h3>
                <p className="mt-2 text-sm text-[#3F5F68]">{it[3].d}</p>
              </div>
              <p className="font-display text-7xl leading-none text-[#C2502F] md:text-8xl">0g</p>
            </Card>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-3">
            <Card testid="bento-travel" className="flex h-full items-center gap-6 border border-[#073B4C]/12 bg-white/60">
              <div className="min-w-0 flex-1">
                <Plane className="h-6 w-6 text-[#0A7189]" />
                <h3 className="mt-4 text-xl font-semibold">{it[4].t}</h3>
                <p className="mt-2 text-sm text-[#3F5F68]">{it[4].d}</p>
              </div>
              <img src="/img/left.png" alt="" className="h-32 w-24 shrink-0 object-contain" />
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
